import { apiInstance } from '@/api/api.js'

const api = apiInstance()

export default {
  async getCourseDefinitions({ commit, dispatch }, params) {
    let isLibrary = params?.library === true
    let commitSufix = isLibrary ? 'LibraryCourses' : 'MyCourses'
    try {
      commit(`setLoading${commitSufix}`, true)

      const response = await api.get('/course-definitions', { params })

      commit(`set${commitSufix}`, response?.data?.content || [])
      commit(`setLoading${commitSufix}`, false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit(`setLoading${commitSufix}`, false)
    }
  },
  async getCourseDefinitionParticipants({ commit, dispatch }, { id }) {
    try {
      commit('setLoadingCurrentCourseParticipants', true)
      const response = await api.get(`/course-definitions/${id}/participants`)
      commit('setCurrentCourseParticipants', response?.data?.content || [])
      commit(
        'setCurrentCourseAssignmentStatus',
        response?.data?.assignmentStatus,
      )
      commit('setLoadingCurrentCourseParticipants', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoadingCurrentCourseParticipants', false)
    }
  },
  async createCourseDefinition({ commit, dispatch }, data) {
    try {
      commit('setLoadingCurrentCourse', true)

      const model = {
        title: data?.title || '',
        description: data?.description || '',
        visibilityStatus: data?.visibilityStatus || 'DRAFT',
      }

      let file = null

      if (data?.file === null || data?.file === undefined) {
        // do not change thumbnail
        model.media = null
      } else if (data?.file?.mlSourceResourceId) {
        // select new thumbnail from media library
        model.media = { mlSourceResourceId: data?.file?.mlSourceResourceId }
      } else if (data?.file?.staticMediaId) {
        // select new thubmnail from mobile static media
        model.media = { staticMediaId: data?.file?.staticMediaId }
      } else if (data?.file?.remove) {
        // remove thumbnail
        model.media = { remove: true }
      } else {
        // upload new thumbnail from local device
        file = data.file
      }

      let formData = new FormData()

      if (file) {
        formData.append(
          'file',
          new Blob([file.file], {
            type: 'application/octet-stream',
          }),
          file.name,
        )

        // remove attach ml file if there is file upload from computer included
        delete data['media']
      }

      formData.append(
        'model',
        new Blob([JSON.stringify(model)], {
          type: 'application/json',
        }),
      )

      const response = await api.post('/course-definitions', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      commit('setLoadingCurrentCourse', false)
      return response.data
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoadingCurrentCourse', false)
    }
  },
  async getCourseDefinition({ commit, dispatch }, { id }) {
    try {
      commit('setLoadingCurrentCourse', true)
      const response = await api.get(`/course-definitions/${id}`)
      commit('setCurrentCourse', response.data)
      commit('setLoadingCurrentCourse', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setCurrentCourse', null)
      commit('setLoadingCurrentCourse', false)
    }
  },
  async updateCourseDefinition(
    { commit, dispatch },
    { data, successMessage = 'Course updated!' },
  ) {
    try {
      commit('setLoadingCurrentCourse', true)
      const id = data?.id

      if (!id) {
        commit('appStates/setSnackbarNotification', 'Cannot find course ID!', {
          root: true,
        })
        commit('setLoadingCurrentCourse', false)
        return
      }
      const model = {
        title: data?.title || '',
        description: data?.description || '',
        visibilityStatus: data?.visibilityStatus || 'DRAFT',
      }

      let file = null

      if (data?.file === null || data?.file === undefined) {
        // do not change thumbnail
        model.media = null
      } else if (data?.file?.mlSourceResourceId) {
        // select new thumbnail from media library
        model.media = { mlSourceResourceId: data?.file?.mlSourceResourceId }
      } else if (data?.file?.staticMediaId) {
        // select new thubmnail from mobile static media
        model.media = { staticMediaId: data?.file?.staticMediaId }
      } else if (data?.file?.remove) {
        // remove thumbnail
        model.media = { remove: true }
      } else {
        // upload new thumbnail from local device
        file = data.file
      }

      let formData = new FormData()

      if (file) {
        formData.append(
          'file',
          new Blob([file.file], {
            type: 'application/octet-stream',
          }),
          file.name,
        )

        // remove attach ml file if there is file upload from computer included
        delete data['media']
      }

      formData.append(
        'model',
        new Blob([JSON.stringify(model)], {
          type: 'application/json',
        }),
      )

      await api.put(`/course-definitions/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      dispatch('getCourseDefinition', { id })

      commit('setLoadingCurrentCourse', false)

      commit('appStates/setSnackbarNotification', successMessage, {
        root: true,
      })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoadingCurrentCourse', false)
      throw error
    }
  },
  async assignCourseDefinition(
    { commit, dispatch },
    { id, all = false, autoAssignAll = false, participants = [] },
  ) {
    try {
      if (
        participants.length === 0 &&
        all === false &&
        autoAssignAll === false
      ) {
        commit(
          'appStates/setSnackbarNotification',
          'You need select at least one participant!',
          {
            root: true,
          },
        )
        return
      }
      commit('setLoadingCurrentCourse', true)
      await api.post(`/course-definitions/${id}/assign`, {
        all: autoAssignAll || all,
        autoAssignAll,
        participants,
      })
      commit('setLoadingCurrentCourse', false)
      dispatch('getCourseDefinition', { id })
      commit(
        'appStates/setSnackbarNotification',
        'Participants assigned to the Course!',
        {
          root: true,
        },
      )
      return true
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoadingCurrentCourse', false)
    }
  },

  async importCourseDefinition({ commit, dispatch }, { id }) {
    try {
      commit('setLoadingCurrentCourse', true)
      await api.post(`/course-definitions/${id}/library-import`)
      commit('setLoadingLibraryCourses', false)
      commit('appStates/setSnackbarNotification', 'Course imported!', {
        root: true,
      })
      return true
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoadingLibraryCourses', false)
    }
  },
  async deleteCourseDefinition({ commit, dispatch }, { id }) {
    try {
      commit('setLoadingCurrentCourse', true)
      await api.delete(`/course-definitions/${id}`)
      commit('setLoadingCurrentCourse', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoadingCurrentCourse', false)
    }
  },
  async getCourseSteps({ commit, dispatch }, { courseId }) {
    try {
      commit('setLoadingCurrentCourseSteps', true)
      const response = await api.get(`/course-definitions/${courseId}/steps`)
      commit('setCurrentCourseSteps', response?.data?.content || [])
      commit('setLoadingCurrentCourseSteps', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setCurrentCourseSteps', [])
      commit('setLoadingCurrentCourseSteps', false)
    }
  },
  async createCourseStep({ commit, dispatch }, { courseId, data }) {
    try {
      commit('setLoadingCurrentCourseStep', true)

      const model = {
        title: data?.title || '',
        description: data?.description || '',
      }

      if (data?.link) {
        model.link = data.link
      }

      let file = null

      if (data?.file === null || data?.file === undefined) {
        // do not change thumbnail
        model.media = null
      } else if (data?.file?.mlSourceResourceId) {
        // select new thumbnail from media library
        model.media = { mlSourceResourceId: data?.file?.mlSourceResourceId }
      } else if (data?.file?.staticMediaId) {
        // select new thubmnail from mobile static media
        model.media = { staticMediaId: data?.file?.staticMediaId }
      } else if (data?.file?.remove) {
        // remove thumbnail
        model.media = { remove: true }
      } else {
        // upload new thumbnail from local device
        file = data.file
      }

      let formData = new FormData()

      if (file) {
        formData.append(
          'file',
          new Blob([file.file], {
            type: 'application/octet-stream',
          }),
          file.name,
        )

        // remove attach ml file if there is file upload from computer included
        delete data['media']
      }

      formData.append(
        'model',
        new Blob([JSON.stringify(model)], {
          type: 'application/json',
        }),
      )

      await api.post(`/course-definitions/${courseId}/steps`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      dispatch('getCourseSteps', { courseId })

      commit('setLoadingCurrentCourseStep', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoadingCurrentCourseStep', false)
    }
  },
  async deleteCourseStep({ commit, dispatch }, { courseId, stepId }) {
    try {
      commit('setLoadingCurrentCourseSteps', true)
      await api.delete(`/course-definitions/${courseId}/steps/${stepId}`)
      dispatch('getCourseSteps', { courseId })
      commit('setLoadingCurrentCourseSteps', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoadingCurrentCourseSteps', false)
    }
  },
  async updateCourseStep({ commit, dispatch }, { courseId, stepId, data }) {
    try {
      commit('setLoadingCurrentCourseStep', true)

      const model = {
        title: data?.title || '',
        description: data?.description || '',
        link: data.link || '',
      }

      let file = null

      if (data?.file === null || data?.file === undefined) {
        // do not change thumbnail
        model.media = null
      } else if (data?.file?.mlSourceResourceId) {
        // select new thumbnail from media library
        model.media = { mlSourceResourceId: data?.file?.mlSourceResourceId }
      } else if (data?.file?.staticMediaId) {
        // select new thubmnail from mobile static media
        model.media = { staticMediaId: data?.file?.staticMediaId }
      } else if (data?.file?.remove) {
        // remove thumbnail
        model.media = { remove: true }
      } else {
        // upload new thumbnail from local device
        file = data.file
      }

      let formData = new FormData()

      if (file) {
        formData.append(
          'file',
          new Blob([file.file], {
            type: 'application/octet-stream',
          }),
          file.name,
        )

        // remove attach ml file if there is file upload from computer included
        delete data['media']
      }

      formData.append(
        'model',
        new Blob([JSON.stringify(model)], {
          type: 'application/json',
        }),
      )

      const response = await api.put(
        `/course-definitions/${courseId}/steps/${stepId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      dispatch('getCourseSteps', { courseId })
      commit('setLoadingCurrentCourseStep', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setCurrentCourseSteps', [])
      commit('setLoadingCurrentCourseStep', false)
    }
  },
  async moveCourseStep(
    { commit, dispatch },
    { courseId, stepId, prevStep, nextStep },
  ) {
    try {
      const previousStepId = prevStep?.id || null
      const nextStepId = nextStep?.id || null

      if (!previousStepId && !nextStepId) return

      await api.put(`/course-definitions/${courseId}/steps/${stepId}/move`, {
        previousStepId,
        nextStepId,
      })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      dispatch('getCourseSteps', { courseId })
    }
  },
}
